import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';
import {AttrsSchema, Component} from 'panel';

import {computeBarChartTooltipSections} from 'common/report/util/insights/tooltip';
import * as colors from 'common/util/colors';
import {abbreviateNumber, commaizeNumber} from 'common/util';
import {formatPercent} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {determineTooltipPosition, TOOLTIP_THROTTLE_TIME, TooltipPosition} from 'common/components/chart-tooltip/util';
import type {TooltipSection} from 'common/components/chart-tooltip/types';
import type {MetricClauseData} from 'common/report/util/insights/tooltip';

import {template} from './template';
import css from './index.styl?inline';

interface BarChartJsxProps {
  attrs?: BarChartAttrs;
}
interface BarChartState {
  tooltip: {
    sections: TooltipSection[];
    label: string;
    style: TooltipPosition;
  };
}
export interface BarChartAttrs {
  'is-compact'?: boolean;
  params: {
    segments: Array<{
      label: string;
      value: number;
      metricKey: string;
      breakdownSegments: string[];
      barWidth: number;
      percentage: number;
      visibleAmount: string;
      // Need to find a better example of usage, this just makes tsc happy with the existing template
      timeComparisonVisibleAmount?: string;
      timeComparisonPercentage?: number;
      timeComparisonValue?: number;
      timeComparisonBarWidth?: number;
    }>;
    metricClauseByKey: {
      [x: string]: MetricClauseData;
    };
  };
}

export class BarChart extends Component<BarChartState, BarChartAttrs> {
  static get attrsSchema(): AttrsSchema<BarChartAttrs> {
    return {
      ...super.attrsSchema,
      'is-compact': `boolean`,
      params: `json`,
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        tooltip: {
          sections: null,
          label: null,
          style: null,
        },
      },
      helpers: {
        formatPercent,
        ...super.config.helpers,
        getSegmentColor: (idx) => colors.getChartSegmentColorCombo(idx, `default`).primary,
        getTimeComparisonStyle: (color, timeComparisonBarWidth) => {
          return {
            'background-image': `repeating-linear-gradient(-45deg, ${color}, ${color} 25%, #FFF 25%, #FFF 50%, ${color} 50%)`,
            'background-size': `3px 3px`,
            width: `${Math.abs(timeComparisonBarWidth)}%`,
          };
        },
        getInsideTextColor: (idx) => colors.getChartSegmentColorCombo(idx, `default`).secondary,
        getPrettifiedCount: (rawCount) =>
          isNumber(rawCount) && !Number.isInteger(rawCount) ? abbreviateNumber(rawCount, 2) : commaizeNumber(rawCount),
        mousedOverSegment: throttle(
          (ev, segment, color) => {
            const {
              dateRange,
              label,
              metricKey,
              breakdownSegments,
              percentage,
              value,
              timeComparisonDateRange,
              timeComparisonPercentage,
              timeComparisonValue,
            } = segment;

            const timeComparisonStat = timeComparisonDateRange
              ? {
                  count: timeComparisonValue,
                  dateRange: timeComparisonDateRange,
                  percent: timeComparisonPercentage,
                }
              : null;

            const {metricClauseByKey} = this.attr(`params`);
            const metricClauseData = metricClauseByKey[metricKey];
            const sections = computeBarChartTooltipSections({
              metricClauseData,
              segments: breakdownSegments,
              stat: {
                count: value,
                dateRange,
                percent: percentage,
              },
              timeComparisonStat,
              titleColor: color,
            });

            this.update({
              tooltip: {
                sections,
                label,
                style: determineTooltipPosition({
                  ev,
                  chartClasses: this.chartClasses,
                }),
              },
            });
          },
          TOOLTIP_THROTTLE_TIME,
          {leading: true, trailing: false},
        ),
        onMouseLeave: () => {
          this.update({
            tooltip: {
              sections: null,
              label: null,
              style: null,
            },
          });
        },
      },
    };
  }

  get chartClasses() {
    return {
      mainClass: `segment-data`,
      containerClass: `segments`,
    };
  }
}

export const BarChartEl = defineAndMakeJsxFactory<BarChartJsxProps>(`mp-bar-chart`, BarChart);
